import { useEffect, useState } from "react"
import { useFileboyVideo } from "./useFileboyVideo"

const FILEBOY_CC_KEY_BASE = `fileboy_video_cc_`

export function useTextTrack(
  videoId: string,
  options: { defaultCaption?: string | null },
  apiUrl?: string,
) {
  const lsKey = `${FILEBOY_CC_KEY_BASE}_${videoId}`

  const fileboyVideo = useFileboyVideo(apiUrl)
  const [availableCaptions, setAvailableCaptions] = useState<
    { id: string; src: string; lang: string }[]
  >([])
  const [currentCaptionId, setCurrentCaptionId] = useState<string | undefined>()

  useEffect(() => {
    const fn = async () => {
      if (availableCaptions.length > 0) {
        setAvailableCaptions([])
      }
      if (currentCaptionId) {
        setCurrentCaptionId(undefined)
      }

      const videoData = await fileboyVideo.loadData(videoId)

      const captions = videoData.transcripts.map(caption => ({
        src: fileboyVideo.transcriptUrl(videoId, caption.id),
        lang: caption.language,
        id: caption.id,
      }))

      setAvailableCaptions(captions)

      if (options.defaultCaption !== undefined) {
        if (options.defaultCaption === null) {
          setCurrentCaptionId(undefined)
        } else {
          const caption = captions.find(caption => caption.lang === options.defaultCaption)
          setCurrentCaptionId(caption?.id)
        }
      } else {
        const previousCaptionId = localStorage.getItem(lsKey)
        if (previousCaptionId && captions.find(caption => caption.id === previousCaptionId)) {
          setCurrentCaptionId(previousCaptionId)
        } else {
          localStorage.setItem(lsKey, "")
        }
      }
    }
    fn().catch(err => {
      console.error("Failed loading transcripts", err)
    })
  }, [videoId])

  useEffect(() => {
    if (availableCaptions.length > 0 && typeof options.defaultCaption !== "undefined") {
      if (options.defaultCaption === null) {
        setCurrentCaptionId(undefined)
      } else {
        const caption = availableCaptions.find(caption => caption.lang === options.defaultCaption)
        setCurrentCaptionId(caption?.id)
      }
    }
  }, [options.defaultCaption])

  function handleSetCurrentCaptionId(id?: string) {
    localStorage.setItem(lsKey, id ?? "")
    setCurrentCaptionId(id)
  }

  return { availableCaptions, currentCaptionId, handleSetCurrentCaptionId }
}
